import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Empty, message, Popover, Select, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";

import useScheduleDraftEditorWorkspace from "../../hooks/useScheduleDraftEditorWorkspace";
import { scheduleDraftQueryKeys } from "../../hooks/queryKeys";
import { scheduleDraftDataService } from "../../services/scheduleDraftDataService";
import {
    getEditingTracks,
    getReferenceDateShiftTarget,
    getReferenceDisplayTracks,
    getTrackFlights,
    LIVE_REFERENCE_TRACK_TYPE,
    REFERENCE_TRACK_TYPE,
    toArray,
} from "../../lib/trackUtils";
import { countUnmatchedFlightDetails, formatUnmatchedCompareReason } from "../../lib/compareMatchGateUtils";
import TimelineCompareView, { getReferenceFlightSourceId } from "./timeline/TimelineCompareView";
import CompareMatchTable from "./CompareMatchTable";
import { formatScheduleDraftError } from "../../lib/errorUtils";
import { scheduleDraftReferenceDataService } from "../../services/scheduleDraftReferenceDataService";
import {
    TIMELINE_VIEWPORT_SYNC_MODE_ABSOLUTE,
    buildBoundTimelineViewportRange,
    normalizeTimelineViewportRange,
} from "../../timeline/viewportBinding";

const { Text } = Typography;
const PUBLICATION_SNAPSHOT_REFERENCE_TRACK_TYPE = "EXECUTION_BASELINE_SNAPSHOT";

function getSummaryItems(value) {
    return Array.isArray(value) ? value : toArray(value?.items || value?.summaries || value?.data?.items);
}

function getTransactionRiskCount(value) {
    return getSummaryItems(value).reduce((total, item) => total + (Number(item?.total) || 0), 0);
}

function asId(value) {
    return value == null ? null : String(value);
}

function firstObject(...values) {
    return values.find((value) => value && typeof value === "object") || null;
}

function findTrackById(tracks, trackId) {
    const key = asId(trackId);
    return key ? toArray(tracks).find((track) => asId(track?.id) === key) || null : null;
}

function extractEnsuredEditingTrack(result, fallbackTrack) {
    const candidate = firstObject(
        result?.editing_track,
        result?.editingTrack,
        result?.track,
        result?.comparison_pair?.editing_track,
        result?.comparisonPair?.editingTrack,
    );
    const resultTracks = toArray(result?.tracks);
    const matchedTrack = findTrackById(resultTracks, fallbackTrack?.id);
    const ensuredTrack = candidate && (!fallbackTrack?.id || asId(candidate.id) === asId(fallbackTrack.id)) ? candidate : matchedTrack;
    const comparedReferenceTrackId = result?.compared_reference_track_id || result?.reference_track_id || result?.comparison_pair?.reference_track_id || result?.comparisonPair?.referenceTrackId;

    if (ensuredTrack) {
        const mergedTrack = {
            ...fallbackTrack,
            ...ensuredTrack,
            compared_reference_track_id: ensuredTrack.compared_reference_track_id || comparedReferenceTrackId || fallbackTrack?.compared_reference_track_id,
        };
        if (fallbackTrack?.flights) {
            mergedTrack.flights = fallbackTrack.flights;
        }
        return mergedTrack;
    }

    return comparedReferenceTrackId ? { ...fallbackTrack, compared_reference_track_id: comparedReferenceTrackId } : fallbackTrack;
}

function extractEnsuredReferenceTrack(result) {
    return firstObject(
        result?.reference_track,
        result?.referenceTrack,
        result?.baseline_track,
        result?.baselineTrack,
        result?.comparison_pair?.reference_track,
        result?.comparisonPair?.referenceTrack,
    );
}

function getMatchEffectiveStatus(match, { publicationReview = false } = {}) {
    if (publicationReview) {
        return String(match?.match_status || "UNMATCHED").toUpperCase();
    }
    if (match?.is_valid === false) {
        return "INVALID";
    }
    return String(match?.effective_status || match?.match_status || "UNKNOWN").toUpperCase();
}

function buildStatusCounts(matches = [], options = {}) {
    return toArray(matches).reduce(
        (counts, match) => {
            const status = getMatchEffectiveStatus(match, options);
            if (status === "MATCHED") {
                counts.matched += 1;
            } else if (status === "NEW") {
                counts.new += 1;
            } else if (status === "CANCELLED") {
                counts.cancelled += 1;
            } else if (status === "IGNORE") {
                counts.ignore += 1;
            } else if (status === "UNMATCHED") {
                counts.unmatched += 1;
            } else if (status === "INVALID") {
                counts.invalid += 1;
            }
            return counts;
        },
        { matched: 0, new: 0, cancelled: 0, ignore: 0, unmatched: 0, invalid: 0 },
    );
}

function filterComparisonMatches(matches, editingTrackId, referenceTrackId) {
    const editingKey = asId(editingTrackId);
    const referenceKey = asId(referenceTrackId);
    if (!editingKey || !referenceKey) {
        return [];
    }

    return toArray(matches).filter((match) => {
        const matchTrackId = asId(match?.track_id);
        const matchReferenceTrackId = asId(match?.reference_track_id);
        return (!matchTrackId || matchTrackId === editingKey) && (!matchReferenceTrackId || matchReferenceTrackId === referenceKey);
    });
}

function getComparePairKey(draftId, trackId, referenceTrackId) {
    const draftKey = asId(draftId);
    const trackKey = asId(trackId);
    const referenceKey = asId(referenceTrackId);
    return draftKey && trackKey && referenceKey ? `${draftKey}::${trackKey}::${referenceKey}` : null;
}

function hasAutoMatchRecord(matches = []) {
    return toArray(matches).some((match) => match && String(match.match_type || "AUTO").toUpperCase() === "AUTO");
}

function getSourceParams(track) {
    const metrics = track?.metrics && typeof track.metrics === "object" ? track.metrics : {};
    return metrics.source_params && typeof metrics.source_params === "object" ? metrics.source_params : {};
}

function getComparisonBaselineBlockReason(track) {
    if (!track) {
        return "执行基线不存在";
    }
    if (![LIVE_REFERENCE_TRACK_TYPE, REFERENCE_TRACK_TYPE].includes(track.track_type)) {
        return "不是执行基线";
    }
    if (track.track_type !== LIVE_REFERENCE_TRACK_TYPE) {
        return "不是实时执行基线";
    }
    if (track.is_editable) {
        return "不是只读执行基线";
    }
    if (getReferenceDateShiftTarget(track)) {
        return "日期平移观察轨不能作为执行基线";
    }
    const sourceParams = getSourceParams(track);
    const sourceType = track.source_type || sourceParams.source_type;
    if (sourceType !== "EXECUTION") {
        return "不是执行航班来源";
    }
    return null;
}

function buildBaselineOptions(draft) {
    return getReferenceDisplayTracks(draft)
        .filter((track) => [LIVE_REFERENCE_TRACK_TYPE, REFERENCE_TRACK_TYPE].includes(track?.track_type))
        .map((track) => {
            const reason = getComparisonBaselineBlockReason(track);
            const dateShiftTarget = getReferenceDateShiftTarget(track);
            return {
                value: track.id,
                disabled: Boolean(reason),
                searchText: `${track.name || ""} ${track.track_type || ""} ${track.source_type || ""}`,
                label: (
                    <Space size={6} wrap>
                        <span>{track.name || track.id}</span>
                        {!reason ? <Tag color="green">执行基线</Tag> : null}
                        {dateShiftTarget ? <Tag color="orange">日期平移 {String(dateShiftTarget)}</Tag> : null}
                        {reason ? <Text type="secondary">{reason}</Text> : null}
                    </Space>
                ),
                track,
                reason,
            };
        });
}

function toSourceOperationId(referenceFlight) {
    const rawSourceId = getReferenceFlightSourceId(referenceFlight);
    if (rawSourceId == null || rawSourceId === "") {
        return null;
    }
    const sourceOperationId = Number(rawSourceId);
    return Number.isFinite(sourceOperationId) ? sourceOperationId : null;
}

function stripLocalFlightMetadata(flight) {
    if (!flight || typeof flight !== "object") {
        return flight;
    }
    const plainFlight = { ...flight };
    Object.keys(plainFlight).forEach((field) => {
        if (field.startsWith("_")) {
            delete plainFlight[field];
        }
    });
    return plainFlight;
}

function buildWorkingFlightsPayload(track) {
    return getTrackFlights(track).map(stripLocalFlightMetadata);
}

function isEndpointUnavailable(error) {
    const status = error?.response?.status;
    return status === 404 || status === 405 || status === 501;
}

function getPublicationItems(value) {
    return toArray(value?.items || value?.data?.items || value);
}

function pickReadOnlyPublication(value) {
    const publications = getPublicationItems(value);
    return publications.find((item) => String(item?.status || "").toUpperCase() === "SUCCEEDED") || publications[0] || null;
}

function getSnapshotType(snapshot) {
    return String(snapshot?.snapshot_type || "").toUpperCase();
}

function getSnapshotPayload(snapshot) {
    return snapshot?.payload && typeof snapshot.payload === "object" ? snapshot.payload : {};
}

function compareSnapshotRows(left, right) {
    const leftOrder = Number(left?.display_order);
    const rightOrder = Number(right?.display_order);
    const normalizedLeftOrder = Number.isFinite(leftOrder) ? leftOrder : Number.MAX_SAFE_INTEGER;
    const normalizedRightOrder = Number.isFinite(rightOrder) ? rightOrder : Number.MAX_SAFE_INTEGER;
    if (normalizedLeftOrder !== normalizedRightOrder) {
        return normalizedLeftOrder - normalizedRightOrder;
    }
    return String(left?.captured_at || "").localeCompare(String(right?.captured_at || "")) || String(left?.id || "").localeCompare(String(right?.id || ""));
}

function toSnapshotOperationId(snapshot, payload) {
    const rawValue = snapshot?.source_flight_id ?? payload?.id ?? snapshot?.flight_ref_id;
    if (rawValue == null || rawValue === "") {
        return null;
    }
    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : rawValue;
}

function buildEditingSnapshotFlight(snapshot) {
    const payload = getSnapshotPayload(snapshot);
    return {
        ...payload,
        id: payload.id || snapshot.flight_ref_id,
        track_id: snapshot.track_id || payload.track_id,
        schedule_draft_id: snapshot.schedule_draft_id || payload.schedule_draft_id,
        display_order: snapshot.display_order ?? payload.display_order,
        publication_snapshot_id: snapshot.id,
        publication_snapshot_type: snapshot.snapshot_type,
    };
}

function buildExecutionBaselineSnapshotFlight(snapshot, publication) {
    const payload = getSnapshotPayload(snapshot);
    const sourceOperationId = toSnapshotOperationId(snapshot, payload);
    return {
        ...payload,
        id: `publication-snapshot:${publication?.id || "unknown"}:execution-baseline:${sourceOperationId || snapshot?.flight_ref_id || snapshot?.id}`,
        source_type: "EXECUTION",
        source_id: publication?.reference_track_id ? String(publication.reference_track_id) : null,
        source_operation_id: sourceOperationId,
        source_flight_id: sourceOperationId == null ? null : String(sourceOperationId),
        track_id: publication?.reference_track_id || null,
        change_status: payload.change_status || "UNCHANGED",
        change_source: payload.change_source || "ENGINE",
        display_order: snapshot.display_order ?? payload.display_order,
        effective_departure: payload.effective_departure || payload.etd || payload.std,
        effective_arrival: payload.effective_arrival || payload.eta || payload.sta,
        publication_snapshot_id: snapshot.id,
        publication_snapshot_type: snapshot.snapshot_type,
        source_metadata: {
            ...(payload.source_metadata && typeof payload.source_metadata === "object" ? payload.source_metadata : {}),
            status: payload.status,
            publication_id: publication?.id || snapshot?.publication_id,
            snapshot_id: snapshot.id,
            snapshot_type: snapshot.snapshot_type,
            captured_at: snapshot.captured_at,
            payload_hash: snapshot.payload_hash,
        },
    };
}

function buildPublicationSnapshotTracks(publication, editingTrack, referenceTrack, referenceTrackId) {
    if (!publication) {
        return { editingTrack: null, referenceTrack: null, editingSnapshotCount: 0, baselineSnapshotCount: 0 };
    }
    const snapshots = toArray(publication.snapshots);
    const editingSnapshots = snapshots.filter((snapshot) => getSnapshotType(snapshot) === "EDITING_TRACK").sort(compareSnapshotRows);
    const baselineSnapshots = snapshots.filter((snapshot) => getSnapshotType(snapshot) === "EXECUTION_BASELINE").sort(compareSnapshotRows);
    const snapshotMeta = {
        publication_id: publication.id,
        publication_status: publication.status,
        captured_at_min: publication.snapshot_summary?.captured_at_min,
        captured_at_max: publication.snapshot_summary?.captured_at_max,
    };
    const resolvedReferenceTrackId = asId(publication.reference_track_id || referenceTrackId || referenceTrack?.id);

    return {
        editingTrack: editingSnapshots.length
            ? {
                  ...editingTrack,
                  is_editable: false,
                  flights: editingSnapshots.map(buildEditingSnapshotFlight),
                  publication_snapshot: snapshotMeta,
              }
            : null,
        referenceTrack: baselineSnapshots.length
            ? {
                  ...(referenceTrack || {}),
                  id: resolvedReferenceTrackId || `publication:${publication.id}:execution-baseline`,
                  schedule_draft_id: publication.schedule_draft_id,
                  name: referenceTrack?.name || "发布时执行基线",
                  track_type: PUBLICATION_SNAPSHOT_REFERENCE_TRACK_TYPE,
                  source_type: "EXECUTION",
                  source_ref: referenceTrack?.source_ref || null,
                  is_editable: false,
                  metrics: {
                      ...(referenceTrack?.metrics && typeof referenceTrack.metrics === "object" ? referenceTrack.metrics : {}),
                      publication_snapshot: snapshotMeta,
                  },
                  summary: "发布时执行基线快照",
                  flights: baselineSnapshots.map((snapshot) => buildExecutionBaselineSnapshotFlight(snapshot, publication)),
                  publication_snapshot: snapshotMeta,
              }
            : null,
        editingSnapshotCount: editingSnapshots.length,
        baselineSnapshotCount: baselineSnapshots.length,
    };
}

function getCompareConfirmationExpiresInSeconds(confirmation, nowMs = Date.now()) {
    if (!confirmation?.expires_at) {
        return 0;
    }
    const expiresAtMs = Date.parse(confirmation.expires_at);
    if (!Number.isFinite(expiresAtMs)) {
        return Math.max(Number(confirmation.expires_in_seconds || 0), 0);
    }
    return Math.max(Math.ceil((expiresAtMs - nowMs) / 1000), 0);
}

function getCompareConfirmationStatus(confirmation, nowMs = Date.now()) {
    if (!confirmation) {
        return null;
    }
    if (getCompareConfirmationExpiresInSeconds(confirmation, nowMs) <= 0 && String(confirmation.status || "").toUpperCase() === "CONFIRMED") {
        return "EXPIRED";
    }
    return String(confirmation.status || "UNKNOWN").toUpperCase();
}

function useCompareConfirmationExpiryRevision(confirmation) {
    const [, setRevision] = useState(0);

    useEffect(() => {
        if (String(confirmation?.status || "").toUpperCase() !== "CONFIRMED") {
            return undefined;
        }

        const expiresAtMs = Date.parse(confirmation?.expires_at);
        const fallbackDelayMs = Math.max(Number(confirmation?.expires_in_seconds || 0), 0) * 1000;
        const delayMs = Number.isFinite(expiresAtMs) ? expiresAtMs - Date.now() : fallbackDelayMs;
        if (delayMs <= 0) {
            setRevision((current) => current + 1);
            return undefined;
        }

        const timerId = window.setTimeout(() => setRevision((current) => current + 1), delayMs + 250);
        return () => window.clearTimeout(timerId);
    }, [confirmation?.expires_at, confirmation?.expires_in_seconds, confirmation?.status]);
}

function CompareConfirmationStatusTag({ confirmation, readOnly = false }) {
    const [nowTick, setNowTick] = useState(() => Date.now());
    const status = getCompareConfirmationStatus(confirmation, nowTick);
    const expiresIn = getCompareConfirmationExpiresInSeconds(confirmation, nowTick);
    const valid = Boolean(confirmation?.valid_for_publish && status === "CONFIRMED" && expiresIn > 0 && !confirmation?.current_issue);

    useEffect(() => {
        setNowTick(Date.now());
    }, [confirmation?.expires_at, confirmation?.status]);

    useEffect(() => {
        if (readOnly || !valid) {
            return undefined;
        }
        const timerId = window.setInterval(() => setNowTick(Date.now()), 1000);
        return () => window.clearInterval(timerId);
    }, [readOnly, valid]);

    if (readOnly) {
        return <Tag color="green">{getReadOnlyConfirmationLabel(status)}</Tag>;
    }

    return <Tag color={valid ? "green" : confirmation ? "orange" : "default"}>{valid ? `已确认 ${expiresIn}s` : "待确认"}</Tag>;
}

function getResultMatches(result) {
    return toArray(result?.matches || result?.preview_matches || result?.auto_matches || result?.committed_matches);
}

function isFinalizedDraft(draft) {
    return String(draft?.status || "").toUpperCase() === "FINALIZED";
}

function isReadOnlyCompareTrack(draft, track) {
    return isFinalizedDraft(draft) || track?.is_editable === false;
}

function getReadOnlyBaselineLabel(referenceTrack, referenceTrackId) {
    if (referenceTrack?.name) {
        return referenceTrack.name;
    }
    if (referenceTrackId) {
        return `发布记录执行基线 ${referenceTrackId}`;
    }
    return "发布时执行基线快照缺失";
}

function getReadOnlyConfirmationLabel(status) {
    if (status === "PUBLISHED") {
        return "已发布";
    }
    if (status === "CONFIRMED") {
        return "发布时已确认";
    }
    return "发布后回看";
}

function getFirstTimelineInstance(instanceMap) {
    return instanceMap.values().next().value || null;
}

const ScheduleDraftCompareSection = ({ timelineRef, observationControls, onHoverTargetChange }) => {
    const {
        draftId,
        data: { draft, serverDraft, refetchDraft, localEditState, localCompareMatchState, airportConstraintsMap = {} },
    } = useScheduleDraftEditorWorkspace();
    const editingTracks = useMemo(() => getEditingTracks(draft), [draft]);
    const baselineOptions = useMemo(() => buildBaselineOptions(draft), [draft]);
    const compareTimelineRefs = useRef(new Map());
    const compareViewportMetasRef = useRef(new Map());
    const isCompareViewportSyncingRef = useRef(false);
    const compareViewportMetas = useMemo(() => new Map(editingTracks.map((editingTrack) => {
        const viewportId = `compare:${editingTrack.id}`;
        return [viewportId, {
            id: viewportId,
            groupId: `compare:${draftId || "draft"}`,
            syncMode: TIMELINE_VIEWPORT_SYNC_MODE_ABSOLUTE,
            editingTrackId: editingTrack.id,
        }];
    })), [draftId, editingTracks]);

    compareViewportMetasRef.current = compareViewportMetas;

    const handleCompareViewportRangeChange = useCallback((event) => {
        if (isCompareViewportSyncingRef.current) {
            return;
        }

        const sourceId = event?.viewportId;
        const sourceMeta = sourceId ? compareViewportMetasRef.current.get(sourceId) : null;
        const sourceRange = normalizeTimelineViewportRange(event?.range);
        if (!sourceId || !sourceMeta || !sourceRange) {
            return;
        }

        isCompareViewportSyncingRef.current = true;
        try {
            compareTimelineRefs.current.forEach((instance, targetId) => {
                if (targetId === sourceId) {
                    return;
                }
                const targetMeta = compareViewportMetasRef.current.get(targetId);
                const targetRange = buildBoundTimelineViewportRange(sourceRange, sourceMeta, targetMeta);
                if (targetRange) {
                    instance?.setWindow?.(targetRange, { sourceId, source: event.source });
                }
            });
        } finally {
            isCompareViewportSyncingRef.current = false;
        }
    }, []);

    const setCompareTimelineInstance = useCallback((viewportId) => (instance) => {
        if (instance) {
            compareTimelineRefs.current.set(viewportId, instance);
            return;
        }

        compareTimelineRefs.current.delete(viewportId);
    }, []);

    useEffect(() => {
        if (!timelineRef) {
            return undefined;
        }

        timelineRef.current = {
            zoomIn: () => getFirstTimelineInstance(compareTimelineRefs.current)?.zoomIn?.(),
            zoomOut: () => getFirstTimelineInstance(compareTimelineRefs.current)?.zoomOut?.(),
            fit: () => {
                isCompareViewportSyncingRef.current = true;
                try {
                    compareTimelineRefs.current.forEach((instance) => instance?.fit?.());
                } finally {
                    isCompareViewportSyncingRef.current = false;
                }
            },
            redraw: () => compareTimelineRefs.current.forEach((instance) => instance?.redraw?.()),
        };

        return () => {
            if (timelineRef.current) {
                timelineRef.current = null;
            }
        };
    }, [timelineRef]);

    return (
        <Card className="schedule-draft-main-card schedule-draft-compare-card" bordered={false}>
            <div className="schedule-draft-main-card-header schedule-draft-compare-header schedule-draft-observation-header">
                <div className="schedule-draft-observation-header-actions schedule-draft-observation-header-actions-primary">{observationControls}</div>
            </div>

            {!editingTracks.length ? (
                <Empty description="当前航班计划没有草稿轨" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
                <div className="schedule-draft-compare-panel-list">
                    {editingTracks.map((editingTrack) => {
                        const viewportId = `compare:${editingTrack.id}`;
                        return (
                            <ComparisonTrackPanel
                                key={editingTrack.id}
                                draft={draft}
                                serverDraft={serverDraft}
                                draftId={draftId}
                                editingTrack={editingTrack}
                                baselineOptions={baselineOptions}
                                timelineRef={setCompareTimelineInstance(viewportId)}
                                viewportBinding={{ ...compareViewportMetas.get(viewportId), onRangeChange: handleCompareViewportRangeChange }}
                                refetchDraft={refetchDraft}
                                localEditState={localEditState}
                                localCompareMatchState={localCompareMatchState}
                                airportConstraintsMap={airportConstraintsMap}
                                onHoverTargetChange={onHoverTargetChange}
                            />
                        );
                    })}
                </div>
            )}
        </Card>
    );
};

const ComparisonTrackPanel = ({
    draft,
    serverDraft,
    draftId,
    editingTrack,
    baselineOptions,
    timelineRef,
    viewportBinding,
    refetchDraft,
    localEditState,
    localCompareMatchState,
    airportConstraintsMap = {},
    onHoverTargetChange,
}) => {
    const [previewingMatches, setPreviewingMatches] = useState(false);
    const [committingMatches, setCommittingMatches] = useState(false);
    const [confirmingCompare, setConfirmingCompare] = useState(false);
    const [selectedReferenceTrackId, setSelectedReferenceTrackId] = useState(null);
    const [committedReferenceTrackId, setCommittedReferenceTrackId] = useState(null);
    const autoPreviewTriggeredPairKeysRef = useRef(new Set());
    const flightDirty = Boolean(localEditState?.dirtyState?.isDirty);
    const compareDirty = Boolean(localCompareMatchState?.dirtyState?.isDirty);
    let baselineDirtyBlockReason = "";
    if (flightDirty) {
        baselineDirtyBlockReason = "请先保存或放弃本地航班修改后再进入匹配审查";
    } else if (compareDirty) {
        baselineDirtyBlockReason = "请先保存或放弃本地匹配修改后再重新进入匹配审查";
    }
    const isReadOnlyCompare = isReadOnlyCompareTrack(draft, editingTrack);
    const ensureQuery = useQuery(
        scheduleDraftQueryKeys.comparisonBaseline(draftId, editingTrack?.id),
        () => scheduleDraftDataService.ensureComparisonBaseline(draftId, editingTrack.id),
        {
            enabled: Boolean(draftId && editingTrack?.id && !baselineDirtyBlockReason && !isReadOnlyCompare),
            retry: false,
            staleTime: 0,
            refetchOnWindowFocus: false,
            onSuccess: () => {
                if (!localEditState?.dirtyState?.isDirty && !localCompareMatchState?.dirtyState?.isDirty) {
                    refetchDraft?.();
                }
            },
        },
    );

    const draftEditingTrack = useMemo(() => findTrackById(draft?.tracks, editingTrack?.id) || editingTrack, [draft?.tracks, editingTrack]);
    const ensuredEditingTrack = useMemo(() => extractEnsuredEditingTrack(ensureQuery.data, draftEditingTrack), [draftEditingTrack, ensureQuery.data]);
    const ensuredReferenceTrack = useMemo(() => extractEnsuredReferenceTrack(ensureQuery.data), [ensureQuery.data]);
    const draftPersistedReferenceTrackId = asId(draftEditingTrack?.compared_reference_track_id);
    const ensurePersistedReferenceTrackId = asId(
        ensureQuery.data?.compared_reference_track_id
            || ensureQuery.data?.reference_track_id
            || ensureQuery.data?.comparison_pair?.reference_track_id
            || ensureQuery.data?.comparisonPair?.referenceTrackId
            || ensuredReferenceTrack?.id
            || ensuredEditingTrack?.compared_reference_track_id,
    );
    const persistedReferenceTrackId = asId(committedReferenceTrackId || draftPersistedReferenceTrackId || ensurePersistedReferenceTrackId);
    const persistedReferenceTrack = useMemo(
        () => findTrackById(draft?.tracks, persistedReferenceTrackId) || (asId(ensuredReferenceTrack?.id) === persistedReferenceTrackId ? ensuredReferenceTrack : null),
        [draft?.tracks, ensuredReferenceTrack, persistedReferenceTrackId],
    );
    const readOnlyPublicationListParams = useMemo(() => ({ track_id: editingTrack?.id, limit: 10 }), [editingTrack?.id]);
    const readOnlyPublicationListQuery = useQuery(
        scheduleDraftQueryKeys.publications(draftId, readOnlyPublicationListParams),
        () => scheduleDraftDataService.listPublications(draftId, readOnlyPublicationListParams),
        {
            enabled: Boolean(isReadOnlyCompare && draftId && editingTrack?.id),
            staleTime: 0,
            refetchOnWindowFocus: false,
        },
    );
    const readOnlyPublicationSummary = useMemo(() => pickReadOnlyPublication(readOnlyPublicationListQuery.data), [readOnlyPublicationListQuery.data]);
    const readOnlyPublicationId = asId(readOnlyPublicationSummary?.id);
    const readOnlyPublicationQuery = useQuery(
        scheduleDraftQueryKeys.publication(draftId, readOnlyPublicationId),
        () => scheduleDraftDataService.getPublication(draftId, readOnlyPublicationId),
        {
            enabled: Boolean(isReadOnlyCompare && draftId && readOnlyPublicationId),
            staleTime: 0,
            refetchOnWindowFocus: false,
        },
    );
    const readOnlyPublication = readOnlyPublicationQuery.data || readOnlyPublicationSummary || null;
    const readOnlyReferenceTrackId = asId(readOnlyPublication?.reference_track_id || persistedReferenceTrackId);
    const publicationSnapshotTracks = useMemo(
        () => buildPublicationSnapshotTracks(readOnlyPublication, draftEditingTrack, persistedReferenceTrack, readOnlyReferenceTrackId),
        [draftEditingTrack, persistedReferenceTrack, readOnlyPublication, readOnlyReferenceTrackId],
    );
    const readOnlySnapshotLoading = Boolean(isReadOnlyCompare && (readOnlyPublicationListQuery.isFetching || (readOnlyPublicationId && readOnlyPublicationQuery.isFetching)));
    const activeReferenceTrackId = isReadOnlyCompare ? readOnlyReferenceTrackId || asId(publicationSnapshotTracks.referenceTrack?.id) : selectedReferenceTrackId || persistedReferenceTrackId;
    const activeReferenceTrack = useMemo(() => {
        if (!activeReferenceTrackId) {
            return null;
        }
        if (isReadOnlyCompare) {
            return publicationSnapshotTracks.referenceTrack || null;
        }
        if (!ensureQuery.isSuccess && !isReadOnlyCompare) {
            return null;
        }
        return findTrackById(draft?.tracks, activeReferenceTrackId) || (asId(persistedReferenceTrack?.id) === asId(activeReferenceTrackId) ? persistedReferenceTrack : null);
    }, [activeReferenceTrackId, draft?.tracks, ensureQuery.isSuccess, isReadOnlyCompare, persistedReferenceTrack, publicationSnapshotTracks.referenceTrack]);
    const activeReferenceTrackQueryId = isReadOnlyCompare ? readOnlyReferenceTrackId : activeReferenceTrack?.id || null;
    const isLocalBaselineOverride = !isReadOnlyCompare && Boolean(activeReferenceTrackId && asId(activeReferenceTrackId) !== asId(persistedReferenceTrackId));
    const effectiveEditingTrack = useMemo(
        () => {
            const baseTrack = isReadOnlyCompare ? publicationSnapshotTracks.editingTrack : ensuredEditingTrack;
            return baseTrack && activeReferenceTrackId ? { ...baseTrack, compared_reference_track_id: activeReferenceTrackId } : baseTrack;
        },
        [activeReferenceTrackId, ensuredEditingTrack, isReadOnlyCompare, publicationSnapshotTracks.editingTrack],
    );

    useEffect(() => {
        if ((ensureQuery.isSuccess || isReadOnlyCompare) && persistedReferenceTrackId && !selectedReferenceTrackId) {
            setSelectedReferenceTrackId(persistedReferenceTrackId);
        }
    }, [ensureQuery.isSuccess, isReadOnlyCompare, persistedReferenceTrackId, selectedReferenceTrackId]);

    useEffect(() => {
        if (committedReferenceTrackId && draftPersistedReferenceTrackId === committedReferenceTrackId) {
            setCommittedReferenceTrackId(null);
        }
    }, [committedReferenceTrackId, draftPersistedReferenceTrackId]);

    const fallbackMatches = useMemo(
        () => filterComparisonMatches((serverDraft || draft)?.matches, effectiveEditingTrack?.id, activeReferenceTrackQueryId),
        [activeReferenceTrackQueryId, draft, effectiveEditingTrack?.id, serverDraft],
    );

    const matchQuery = useQuery(
        scheduleDraftQueryKeys.matches(draftId, effectiveEditingTrack?.id, activeReferenceTrackQueryId),
        () => scheduleDraftDataService.getTrackMatches(draftId, effectiveEditingTrack.id, activeReferenceTrackQueryId),
        {
            enabled: Boolean(draftId && effectiveEditingTrack?.id && activeReferenceTrackQueryId && !isLocalBaselineOverride),
            staleTime: 0,
            refetchOnWindowFocus: false,
        },
    );

    const confirmationQuery = useQuery(
        scheduleDraftQueryKeys.compareConfirmation(draftId, effectiveEditingTrack?.id, activeReferenceTrackQueryId),
        () => scheduleDraftDataService.getCompareConfirmation(draftId, effectiveEditingTrack.id, activeReferenceTrackQueryId),
        {
            enabled: Boolean(draftId && effectiveEditingTrack?.id && activeReferenceTrackQueryId && !isLocalBaselineOverride),
            staleTime: 0,
            refetchOnWindowFocus: false,
        },
    );

    const queryMatches = useMemo(() => filterComparisonMatches(matchQuery.data?.matches, effectiveEditingTrack?.id, activeReferenceTrackQueryId), [activeReferenceTrackQueryId, effectiveEditingTrack?.id, matchQuery.data?.matches]);
    const ensureMatches = useMemo(() => filterComparisonMatches(ensureQuery.data?.matches, effectiveEditingTrack?.id, activeReferenceTrackQueryId), [activeReferenceTrackQueryId, effectiveEditingTrack?.id, ensureQuery.data?.matches]);
    const canInitializePairFromServer = !isLocalBaselineOverride && (matchQuery.isSuccess || Boolean(matchQuery.error && (ensureMatches.length || fallbackMatches.length)));
    const serverMatches = useMemo(() => {
        if ((!ensureQuery.isSuccess && !isReadOnlyCompare) || isLocalBaselineOverride) {
            return [];
        }
        if (matchQuery.isSuccess) {
            return queryMatches;
        }
        if (matchQuery.error) {
            return ensureMatches.length ? ensureMatches : fallbackMatches;
        }
        return [];
    }, [ensureMatches, ensureQuery.isSuccess, fallbackMatches, isLocalBaselineOverride, isReadOnlyCompare, matchQuery.error, matchQuery.isSuccess, queryMatches]);
    const pairState = localCompareMatchState?.getPairState?.(effectiveEditingTrack?.id, activeReferenceTrackQueryId);
    useEffect(() => {
        if ((!ensureQuery.isSuccess && !isReadOnlyCompare) || !effectiveEditingTrack?.id || !activeReferenceTrackQueryId || !localCompareMatchState?.initPair) {
            return;
        }
        if (!canInitializePairFromServer) {
            return;
        }
        if (pairState && !localCompareMatchState?.dirtyState?.dirtyPairKeys?.has?.(`${asId(effectiveEditingTrack.id)}::${asId(activeReferenceTrackQueryId)}`) && !matchQuery.isSuccess) {
            return;
        }
        localCompareMatchState.initPair({
            trackId: effectiveEditingTrack.id,
            referenceTrackId: activeReferenceTrackQueryId,
            matches: serverMatches,
            serverReferenceTrackId: persistedReferenceTrackId || null,
        });
    }, [activeReferenceTrackQueryId, canInitializePairFromServer, effectiveEditingTrack?.id, ensureQuery.isSuccess, isReadOnlyCompare, localCompareMatchState, matchQuery.isSuccess, pairState, persistedReferenceTrackId, serverMatches]);

    const matches = pairState?.initialized ? pairState.localMatches : serverMatches;
    const referenceOperationIds = useMemo(
        () => [...new Set(getTrackFlights(activeReferenceTrack).map(toSourceOperationId).filter((value) => value != null))],
        [activeReferenceTrack],
    );
    const transactionSummaryQuery = useQuery(
        scheduleDraftQueryKeys.operationTransactionSummaries(referenceOperationIds),
        () => scheduleDraftReferenceDataService.getOperationTransactionSummaries(referenceOperationIds),
        {
            enabled: referenceOperationIds.length > 0,
            staleTime: 30000,
            refetchOnWindowFocus: false,
        },
    );
    const pairDirty = pairState ? localCompareMatchState?.dirtyState?.dirtyPairKeys?.has?.(`${asId(effectiveEditingTrack?.id)}::${asId(activeReferenceTrackQueryId)}`) : false;
    const canUndoCompare = (pairState?.undoStack?.length || 0) > 0;
    const canRedoCompare = (pairState?.redoStack?.length || 0) > 0;
    const statusCounts = useMemo(() => buildStatusCounts(matches, { publicationReview: isReadOnlyCompare }), [isReadOnlyCompare, matches]);
    const unmatchedFlightDetails = useMemo(() => countUnmatchedFlightDetails(effectiveEditingTrack, activeReferenceTrack, matches), [activeReferenceTrack, effectiveEditingTrack, matches]);
    const fallbackUnmatchedCount = unmatchedFlightDetails.total;
    const summaryCounts = {
        matched: statusCounts.matched,
        new: statusCounts.new,
        cancelled: statusCounts.cancelled,
        ignore: statusCounts.ignore,
        invalid: statusCounts.invalid,
        unmatched: Math.max(statusCounts.unmatched, fallbackUnmatchedCount),
    };
    const unmatchedCount = summaryCounts.unmatched;
    const invalidCount = summaryCounts.invalid;
    const compareConfirmation = confirmationQuery.data || null;
    useCompareConfirmationExpiryRevision(compareConfirmation);
    const compareConfirmationNow = Date.now();
    const compareConfirmationStatus = getCompareConfirmationStatus(compareConfirmation, compareConfirmationNow);
    const compareConfirmationExpiresIn = getCompareConfirmationExpiresInSeconds(compareConfirmation, compareConfirmationNow);
    const compareConfirmationValid = Boolean(compareConfirmation?.valid_for_publish && compareConfirmationStatus === "CONFIRMED" && compareConfirmationExpiresIn > 0 && !compareConfirmation?.current_issue);
    const transactionRiskCount = useMemo(() => getTransactionRiskCount(transactionSummaryQuery.data), [transactionSummaryQuery.data]);
    const confirmCompareDisabledReason = (() => {
        if (!draftId || !effectiveEditingTrack?.id) {
            return "缺少草稿或草稿轨 ID";
        }
        if (isReadOnlyCompare) {
            return "该草稿轨已发布，只能查看匹配";
        }
        if (committingMatches) {
            return "正在保存匹配，请稍候";
        }
        if (previewingMatches) {
            return "正在生成自动匹配预览，请稍候";
        }
        if (ensureQuery.isLoading || matchQuery.isFetching) {
            return "正在同步执行基线或匹配记录，请稍候";
        }
        if (ensureQuery.error) {
            return "执行基线同步失败，请选择合法执行基线后重试";
        }
        if (!activeReferenceTrack?.id) {
            return "缺少合法执行基线，请先选择执行基线";
        }
        if (isLocalBaselineOverride) {
            return "执行基线已在本地切换，请先保存匹配";
        }
        if (flightDirty) {
            return "航班有未保存修改，请先保存或放弃本地航班修改";
        }
        if (compareDirty || pairDirty) {
            return "匹配有未保存改动，请先保存匹配";
        }
        if (invalidCount > 0) {
            return `存在 ${invalidCount} 个失效匹配，需重新匹配或处理失效执行航班`;
        }
        if (unmatchedCount > 0) {
            return formatUnmatchedCompareReason(unmatchedCount, unmatchedFlightDetails, statusCounts.unmatched);
        }
        return null;
    })();
    const confirmCompareButtonDisabled = Boolean(confirmCompareDisabledReason) || confirmationQuery.isFetching || confirmingCompare;
    const confirmCompareTooltipTitle = confirmCompareButtonDisabled
        ? confirmCompareDisabledReason || (confirmationQuery.isFetching ? "正在同步匹配确认状态，请稍候" : "正在确认匹配，请稍候")
        : "";

    const buildMatchWorkingPayload = useCallback(() => ({
        reference_track_id: activeReferenceTrack?.id,
        working_flights: buildWorkingFlightsPayload(effectiveEditingTrack),
        manual_matches: localCompareMatchState?.getManualDecisions?.(effectiveEditingTrack?.id, activeReferenceTrack?.id) || [],
        working_matches: localCompareMatchState?.getCommitMatches?.(effectiveEditingTrack?.id, activeReferenceTrack?.id) || [],
        matches: localCompareMatchState?.getCommitMatches?.(effectiveEditingTrack?.id, activeReferenceTrack?.id) || [],
    }), [activeReferenceTrack?.id, effectiveEditingTrack, localCompareMatchState]);

    const runAutoMatch = useCallback(async (options = {}) => {
        const { silent = false, ...payloadOverrides } = options || {};
        if (!draftId || !effectiveEditingTrack?.id) {
            if (!silent) {
                message.error("缺少航班计划或草稿轨 ID，无法自动匹配");
            }
            return null;
        }
        if (!activeReferenceTrack?.id) {
            if (!silent) {
                message.error("当前草稿轨未绑定合法执行基线，无法自动匹配");
            }
            return null;
        }
        setPreviewingMatches(true);
        try {
            const result = await scheduleDraftDataService.autoMatchPreview(draftId, effectiveEditingTrack.id, {
                ...buildMatchWorkingPayload(),
                ...payloadOverrides,
            });
            const previewMatches = getResultMatches(result);
            localCompareMatchState.applyAutoPreview({
                trackId: effectiveEditingTrack.id,
                referenceTrackId: activeReferenceTrack.id,
                matches: previewMatches,
            });
            if (!silent) {
                message.success(`自动匹配完成，生成 ${previewMatches.filter((match) => String(match?.match_type || "").toUpperCase() === "AUTO").length} 条自动结果`);
            }
            return result;
        } catch (error) {
            if (isEndpointUnavailable(error)) {
                if (!silent) {
                    message.warning("自动匹配服务暂不可用，已保留当前本地自动结果");
                }
                return null;
            }
            if (!silent) {
                message.error(formatScheduleDraftError(error, "自动匹配失败"));
            }
            return null;
        } finally {
            setPreviewingMatches(false);
        }
    }, [activeReferenceTrack?.id, buildMatchWorkingPayload, draftId, effectiveEditingTrack?.id, localCompareMatchState]);

    useEffect(() => {
        if (isReadOnlyCompare || !draftId || !effectiveEditingTrack?.id || !activeReferenceTrack?.id) {
            return;
        }
        if (!pairState?.initialized || !ensureQuery.isSuccess || ensureQuery.error) {
            return;
        }
        if (matchQuery.isLoading || matchQuery.isFetching || previewingMatches || committingMatches) {
            return;
        }
        if (!getTrackFlights(effectiveEditingTrack).length) {
            return;
        }

        const pairKey = getComparePairKey(draftId, effectiveEditingTrack.id, activeReferenceTrack.id);
        if (!pairKey || autoPreviewTriggeredPairKeysRef.current.has(pairKey)) {
            return;
        }
        if (hasAutoMatchRecord(pairState.serverMatches) || hasAutoMatchRecord(pairState.localMatches)) {
            return;
        }

        autoPreviewTriggeredPairKeysRef.current.add(pairKey);
        runAutoMatch({ silent: true }).catch(() => {});
    }, [
        activeReferenceTrack?.id,
        committingMatches,
        draftId,
        effectiveEditingTrack,
        ensureQuery.error,
        ensureQuery.isSuccess,
        isReadOnlyCompare,
        matchQuery.isFetching,
        matchQuery.isLoading,
        pairState,
        previewingMatches,
        runAutoMatch,
    ]);

    const handleMatchCreate = useCallback(async (editingFlight, referenceFlight) => {
        if (!effectiveEditingTrack?.id || !editingFlight?.id) {
            message.error("缺少草稿轨或草稿航班 ID，无法建立匹配");
            return;
        }
        if (!activeReferenceTrack?.id) {
            message.error("缺少执行基线 ID，无法建立匹配");
            return;
        }
        const sourceOperationId = toSourceOperationId(referenceFlight);
        if (sourceOperationId == null) {
            message.error("执行航班缺少 ID，无法建立匹配");
            return;
        }

        localCompareMatchState.setManualMatched({
            trackId: effectiveEditingTrack.id,
            referenceTrackId: activeReferenceTrack.id,
            scheduleDraftFlightId: editingFlight.id,
            sourceOperationId,
        });
        message.success("已在本地建立人工匹配，保存匹配后生效");
    }, [activeReferenceTrack?.id, effectiveEditingTrack?.id, localCompareMatchState]);

    const handleMarkNew = useCallback(async (flight) => {
        if (!effectiveEditingTrack?.id || !flight?.id) {
            message.error("缺少草稿轨或草稿航班 ID，无法标为新增");
            return;
        }
        if (!activeReferenceTrack?.id) {
            message.error("缺少执行基线 ID，无法标为新增");
            return;
        }
        localCompareMatchState.markManualNew({
            trackId: effectiveEditingTrack.id,
            referenceTrackId: activeReferenceTrack.id,
            scheduleDraftFlightId: flight.id,
        });
        message.success("已在本地标为新增，保存匹配后生效");
    }, [activeReferenceTrack?.id, effectiveEditingTrack?.id, localCompareMatchState]);

    const handleMarkCancelled = useCallback(async (referenceFlight) => {
        if (!effectiveEditingTrack?.id) {
            message.error("缺少草稿轨 ID，无法标为取消");
            return;
        }
        if (!activeReferenceTrack?.id) {
            message.error("缺少执行基线 ID，无法标为取消");
            return;
        }
        const sourceOperationId = toSourceOperationId(referenceFlight);
        if (sourceOperationId == null) {
            message.error("执行航班缺少 ID，无法标为取消");
            return;
        }
        localCompareMatchState.markManualCancelled({
            trackId: effectiveEditingTrack.id,
            referenceTrackId: activeReferenceTrack.id,
            sourceOperationId,
        });
        message.success("已在本地标为取消，保存匹配后生效");
    }, [activeReferenceTrack?.id, effectiveEditingTrack?.id, localCompareMatchState]);

    const handleMarkIgnore = useCallback(async (role, flight) => {
        if (!effectiveEditingTrack?.id) {
            message.error("缺少草稿轨 ID，无法忽略航班");
            return;
        }
        if (!activeReferenceTrack?.id) {
            message.error("缺少执行基线 ID，无法忽略航班");
            return;
        }

        if (role === "editing") {
            if (!flight?.id) {
                message.error("缺少草稿航班 ID，无法忽略");
                return;
            }
            localCompareMatchState.markManualIgnore({
                trackId: effectiveEditingTrack.id,
                referenceTrackId: activeReferenceTrack.id,
                role,
                scheduleDraftFlightId: flight.id,
            });
            message.success("已在本地忽略草稿航班，保存匹配后生效");
            return;
        }

        const sourceOperationId = toSourceOperationId(flight);
        if (sourceOperationId == null) {
            message.error("执行航班缺少 ID，无法忽略");
            return;
        }
        localCompareMatchState.markManualIgnore({
            trackId: effectiveEditingTrack.id,
            referenceTrackId: activeReferenceTrack.id,
            role,
            sourceOperationId,
        });
        message.success("已在本地忽略执行航班，保存匹配后生效");
    }, [activeReferenceTrack?.id, effectiveEditingTrack?.id, localCompareMatchState]);

    const handleMatchDelete = useCallback(async (match) => {
        if (!effectiveEditingTrack?.id || !activeReferenceTrack?.id || !match) {
            message.error("缺少匹配记录，无法解除匹配");
            return;
        }
        localCompareMatchState.deleteLocalMatch({
            trackId: effectiveEditingTrack.id,
            referenceTrackId: activeReferenceTrack.id,
            match,
        });
        message.success("已在本地解除匹配，保存匹配后生效");
    }, [activeReferenceTrack?.id, effectiveEditingTrack?.id, localCompareMatchState]);

    const handleResetManualMatches = useCallback(() => {
        if (!effectiveEditingTrack?.id || !activeReferenceTrack?.id) {
            message.error("缺少草稿轨或执行基线 ID，无法清空人工决策");
            return;
        }
        if (!window.confirm("清空会删除当前执行基线下的本地人工决策，并保留自动结果。确认清空？")) {
            return;
        }
        localCompareMatchState.resetManualMatches({
            trackId: effectiveEditingTrack.id,
            referenceTrackId: activeReferenceTrack.id,
        });
        message.success("已清空本地人工决策，请按需重新运行自动匹配");
    }, [activeReferenceTrack?.id, effectiveEditingTrack?.id, localCompareMatchState]);

    const handleSaveCompareMatches = useCallback(async () => {
        if (!draftId || !effectiveEditingTrack?.id || !activeReferenceTrack?.id) {
            message.error("缺少草稿、草稿轨或执行基线 ID，无法保存匹配");
            return null;
        }
        if (localEditState?.dirtyState?.isDirty) {
            message.warning("存在未保存的航班修改，请先保存航班，再保存匹配");
            return null;
        }
        setCommittingMatches(true);
        try {
            const payload = buildMatchWorkingPayload();
            const result = await scheduleDraftDataService.commitMatches(draftId, effectiveEditingTrack.id, payload);
            const committedReferenceId = asId(result?.reference_track_id || activeReferenceTrack.id);
            setCommittedReferenceTrackId(committedReferenceId);
            setSelectedReferenceTrackId(committedReferenceId);
            localCompareMatchState.resetPairAfterSave({
                trackId: result?.track_id || effectiveEditingTrack.id,
                referenceTrackId: committedReferenceId,
                matches: getResultMatches(result).length ? getResultMatches(result) : payload.matches,
            });
            if (!isLocalBaselineOverride) {
                await matchQuery.refetch();
            }
            await refetchDraft?.();
            await confirmationQuery.refetch?.();
            message.success("匹配已保存");
            return result;
        } catch (error) {
            const fallback = isEndpointUnavailable(error) ? "保存匹配接口暂不可用，匹配修改未保存" : "保存匹配失败";
            message.error(formatScheduleDraftError(error, fallback));
            return null;
        } finally {
            setCommittingMatches(false);
        }
    }, [activeReferenceTrack?.id, buildMatchWorkingPayload, confirmationQuery, draftId, effectiveEditingTrack?.id, isLocalBaselineOverride, localCompareMatchState, localEditState?.dirtyState?.isDirty, matchQuery, refetchDraft]);

    const handleConfirmCompare = useCallback(async () => {
        if (confirmCompareDisabledReason) {
            message.warning(confirmCompareDisabledReason);
            return null;
        }
        setConfirmingCompare(true);
        try {
            const result = await scheduleDraftDataService.confirmCompare(draftId, effectiveEditingTrack.id, activeReferenceTrack.id);
            await confirmationQuery.refetch?.();
            const seconds = getCompareConfirmationExpiresInSeconds(result);
            message.success(`匹配已确认，请在 ${seconds || 300} 秒内发布`);
            return result;
        } catch (error) {
            message.error(formatScheduleDraftError(error, "确认匹配失败"));
            return null;
        } finally {
            setConfirmingCompare(false);
        }
    }, [activeReferenceTrack?.id, confirmCompareDisabledReason, confirmationQuery, draftId, effectiveEditingTrack?.id]);

    const handleUndoCompare = useCallback(() => {
        localCompareMatchState.undoPair(effectiveEditingTrack?.id, activeReferenceTrack?.id);
    }, [activeReferenceTrack?.id, effectiveEditingTrack?.id, localCompareMatchState]);

    const handleRedoCompare = useCallback(() => {
        localCompareMatchState.redoPair(effectiveEditingTrack?.id, activeReferenceTrack?.id);
    }, [activeReferenceTrack?.id, effectiveEditingTrack?.id, localCompareMatchState]);

    const handleBaselineChange = useCallback(async (referenceTrackId) => {
        if (isReadOnlyCompare) {
            message.info("该草稿轨已发布，只能查看已保存的执行基线");
            return;
        }
        if (localEditState?.dirtyState?.isDirty) {
            message.warning("请先保存或放弃本地航班修改后再切换执行基线");
            return;
        }
        const option = baselineOptions.find((item) => asId(item.value) === asId(referenceTrackId));
        if (!option) {
            message.error("未找到目标执行基线");
            return;
        }
        if (option.reason) {
            message.warning(option.reason);
            return;
        }
        if (!effectiveEditingTrack?.id) {
            message.error("缺少草稿轨 ID，无法切换执行基线");
            return;
        }
        if (asId(referenceTrackId) === asId(activeReferenceTrack?.id)) {
            return;
        }
        if (!window.confirm("切换执行基线会重置当前本地匹配，不会立即写库。确认切换？")) {
            return;
        }

        const previousReferenceTrackId = activeReferenceTrack?.id;
        if (previousReferenceTrackId) {
            localCompareMatchState?.discardPair?.(effectiveEditingTrack.id, previousReferenceTrackId);
        }
        setSelectedReferenceTrackId(asId(referenceTrackId));
        localCompareMatchState?.initPair?.({
            trackId: effectiveEditingTrack.id,
            referenceTrackId,
            matches: [],
            serverReferenceTrackId: persistedReferenceTrackId || previousReferenceTrackId || null,
        });
        message.success("已在本地切换执行基线，保存匹配后生效");
    }, [activeReferenceTrack?.id, baselineOptions, effectiveEditingTrack?.id, isReadOnlyCompare, localCompareMatchState, localEditState?.dirtyState?.isDirty, persistedReferenceTrackId]);

    const baselineDisplayName = isReadOnlyCompare
        ? getReadOnlyBaselineLabel(activeReferenceTrack, activeReferenceTrackId)
        : activeReferenceTrack?.name || (ensureQuery.error ? "未确保" : "同步中");
    const readOnlyHistoryIncomplete = isReadOnlyCompare && !readOnlySnapshotLoading && (!readOnlyPublication || !publicationSnapshotTracks.editingTrack || !publicationSnapshotTracks.referenceTrack);
    const readOnlyHistoryDescription = (() => {
        if (readOnlyPublicationListQuery.error) {
            return formatScheduleDraftError(readOnlyPublicationListQuery.error, "发布历史读取失败，页面不会回退到实时执行基线。");
        }
        if (readOnlyPublicationQuery.error) {
            return formatScheduleDraftError(readOnlyPublicationQuery.error, "发布详情读取失败，页面不会回退到实时执行基线。");
        }
        if (!readOnlyPublication) {
            return "未找到该轨的成功发布记录，页面不会在只读状态补建基线或读取实时执行航班。";
        }
        if (!publicationSnapshotTracks.editingTrack && !publicationSnapshotTracks.referenceTrack) {
            return "该发布记录缺少发布时编辑轨和发布时执行基线快照，页面不会用实时执行基线伪造回看。";
        }
        if (!publicationSnapshotTracks.editingTrack) {
            return "该发布记录缺少发布时编辑轨快照，页面只能提示历史数据不完整。";
        }
        return "该发布记录缺少发布时执行基线快照，页面不会用实时执行基线伪造回看。";
    })();
    const readOnlyMatchHistoryIncomplete = isReadOnlyCompare
        && !readOnlyHistoryIncomplete
        && !matchQuery.isFetching
        && (matchQuery.isSuccess || Boolean(matchQuery.error) || fallbackMatches.length > 0)
        && unmatchedCount;
    const compareReadiness = (() => {
        if (isReadOnlyCompare) {
            return {
                color: readOnlyHistoryIncomplete ? "orange" : "green",
                title: "发布后回看",
                detail: readOnlyHistoryIncomplete ? "发布快照不完整，只能查看已保存事实。" : "只读查看发布时匹配和执行基线快照。",
            };
        }
        if (ensureQuery.isLoading || matchQuery.isFetching || confirmationQuery.isFetching) {
            return { color: "processing", title: "同步中", detail: "正在同步执行基线、匹配记录和确认状态。" };
        }
        if (ensureQuery.error) {
            return { color: "red", title: "不能确认", detail: "执行基线同步失败，请切换或重试合法执行基线。" };
        }
        if (compareConfirmationValid) {
            return { color: "green", title: "可发布", detail: "匹配已确认，当前可发布窗口仍有效。" };
        }
        if (confirmCompareDisabledReason) {
            return { color: "orange", title: "待处理", detail: confirmCompareDisabledReason };
        }
        return { color: "green", title: "可确认匹配", detail: "当前没有未保存、待处理或失效匹配。" };
    })();
    const secondaryCountsContent = (
        <Space direction="vertical" size={6} className="schedule-draft-compare-counts-popover">
            <Text strong>结果分布</Text>
            <Tag color="blue">{isReadOnlyCompare ? "已发布新增" : "新增"} {summaryCounts.new}</Tag>
            <Tag color="volcano">{isReadOnlyCompare ? "已发布取消" : "取消"} {summaryCounts.cancelled}</Tag>
            <Tag color="gold">{isReadOnlyCompare ? "已发布忽略" : "忽略"} {summaryCounts.ignore}</Tag>
            <Tag color="green">{isReadOnlyCompare ? "已替换" : "已匹配"} {summaryCounts.matched}</Tag>
        </Space>
    );
    const transactionRiskTag = transactionSummaryQuery.error ? (
        <Tag color="orange">事务风险 暂不可用</Tag>
    ) : transactionSummaryQuery.isFetching ? (
        <Tag>事务读取中</Tag>
    ) : (
        <Tag color={transactionRiskCount ? "orange" : "default"}>事务风险 {transactionRiskCount}</Tag>
    );
    const compareMoreActionsContent = !isReadOnlyCompare ? (
        <div className="schedule-draft-compare-more-actions">
            <Text strong>执行基线与次级操作</Text>
            <Text type="secondary">切换执行基线、撤销/重做和清空人工决策都会继续受本地工作态保护。</Text>
            <Select
                size="small"
                className="schedule-draft-compare-baseline-select"
                placeholder="执行基线"
                value={activeReferenceTrack?.id}
                disabled={!effectiveEditingTrack?.id}
                options={baselineOptions}
                optionFilterProp="searchText"
                showSearch
                onChange={(value) => handleBaselineChange(value).catch(() => {})}
            />
            <Space wrap size={[8, 8]}>
                <Button size="small" disabled={!canUndoCompare || committingMatches || previewingMatches} onClick={handleUndoCompare}>
                    撤销匹配
                </Button>
                <Button size="small" disabled={!canRedoCompare || committingMatches || previewingMatches} onClick={handleRedoCompare}>
                    重做匹配
                </Button>
                <Button size="small" disabled={!effectiveEditingTrack?.id || !activeReferenceTrack?.id || committingMatches || previewingMatches} onClick={handleResetManualMatches}>
                    清空人工决策
                </Button>
            </Space>
        </div>
    ) : null;

    return (
        <Card className="schedule-draft-compare-panel-card" bordered>
            <div className="schedule-draft-compare-panel-header">
                <div className="schedule-draft-compare-panel-summary">
                    <div className="schedule-draft-section-heading schedule-draft-compare-panel-identity">
                        <Space wrap size={[8, 8]} align="center">
                            <Text strong>匹配审查</Text>
                            <Text>{effectiveEditingTrack?.name || draftEditingTrack?.name || "未命名草稿轨"}</Text>
                            {isReadOnlyCompare ? <Tag color="cyan">发布快照</Tag> : null}
                            {isReadOnlyCompare ? <Tag color="green">已发布只读</Tag> : ensureQuery.isLoading || matchQuery.isFetching ? <Tag color="processing">同步中</Tag> : pairDirty ? <Tag color="orange">本地未保存</Tag> : <Tag>已保存</Tag>}
                            <CompareConfirmationStatusTag confirmation={compareConfirmation} readOnly={isReadOnlyCompare} />
                        </Space>
                        <Text type="secondary">{isReadOnlyCompare ? "发布时执行基线" : "执行基线"}：{baselineDisplayName}</Text>
                    </div>
                    <div className="schedule-draft-compare-readiness">
                        <Tag color={compareReadiness.color}>{compareReadiness.title}</Tag>
                        <Text type="secondary">{compareReadiness.detail}</Text>
                    </div>
                </div>
                <div className="schedule-draft-compare-panel-actions">
                    {!isReadOnlyCompare ? (
                        <Space wrap size={[8, 8]} className="schedule-draft-compare-primary-actions">
                            <Button type="primary" size="small" loading={previewingMatches} disabled={!effectiveEditingTrack?.id || !activeReferenceTrack?.id || ensureQuery.isLoading || Boolean(ensureQuery.error) || committingMatches} onClick={() => runAutoMatch().catch(() => {})}>
                                自动匹配
                            </Button>
                            <Button type="primary" size="small" loading={committingMatches} disabled={!pairDirty || !effectiveEditingTrack?.id || !activeReferenceTrack?.id || previewingMatches} onClick={() => handleSaveCompareMatches().catch(() => {})}>
                                保存匹配
                            </Button>
                            <Tooltip title={confirmCompareTooltipTitle || null}>
                                <span className="schedule-draft-compare-action-wrapper">
                                    <Button size="small" loading={confirmingCompare} disabled={confirmCompareButtonDisabled} onClick={() => handleConfirmCompare().catch(() => {})}>
                                        确认匹配
                                    </Button>
                                </span>
                            </Tooltip>
                            <Popover trigger="click" placement="bottomRight" content={compareMoreActionsContent}>
                                <Button size="small" icon={<MoreOutlined />}>更多操作</Button>
                            </Popover>
                        </Space>
                    ) : null}
                    <Space wrap size={[8, 8]} className="schedule-draft-compare-statusbar">
                        {isReadOnlyCompare ? <Tag color="green">只读回看</Tag> : null}
                        {readOnlyHistoryIncomplete ? <Tag color="orange">发布快照缺失</Tag> : null}
                        {readOnlyMatchHistoryIncomplete ? <Tag color="orange">历史数据不完整 {unmatchedCount}</Tag> : null}
                        <Tag color={unmatchedCount ? "orange" : "default"}>待处理 {unmatchedCount}</Tag>
                        <Tag color={summaryCounts.invalid ? "red" : "default"}>失效 {summaryCounts.invalid}</Tag>
                        {transactionRiskTag}
                        <Popover trigger="click" placement="bottomRight" content={secondaryCountsContent}>
                            <Button size="small">结果分布</Button>
                        </Popover>
                    </Space>
                </div>
            </div>

            {!isReadOnlyCompare && ensureQuery.error ? <Alert type="error" showIcon message="执行基线同步失败" description={formatScheduleDraftError(ensureQuery.error, "请检查执行基线是否可用")} className="schedule-draft-compare-alert" /> : null}
            {!isReadOnlyCompare && baselineDirtyBlockReason && !ensureQuery.data ? <Alert type="warning" showIcon message="已暂停执行基线同步" description={baselineDirtyBlockReason} className="schedule-draft-compare-alert" /> : null}
            {matchQuery.error ? <Alert type="warning" showIcon message="匹配记录加载失败，已回退使用航班计划详情中的匹配快照" description={formatScheduleDraftError(matchQuery.error, "未知错误")} className="schedule-draft-compare-alert" /> : null}
            {!isReadOnlyCompare && compareConfirmation?.current_issue ? <Alert type="warning" showIcon message="匹配确认不可用于发布" description={compareConfirmation.current_issue} className="schedule-draft-compare-alert" /> : null}
            {readOnlyHistoryIncomplete ? <Alert type="warning" showIcon message="发布快照缺失" description={readOnlyHistoryDescription} className="schedule-draft-compare-alert" /> : null}
            {!isReadOnlyCompare && !activeReferenceTrack && !ensureQuery.isLoading && !ensureQuery.error ? <Alert type="warning" showIcon message="当前草稿轨未绑定合法执行基线" description="请先等待系统确保执行基线，或切换到合法的执行基线。" className="schedule-draft-compare-alert" /> : null}

            <Spin spinning={(!isReadOnlyCompare && ensureQuery.isLoading) || readOnlySnapshotLoading || matchQuery.isFetching || confirmationQuery.isFetching || previewingMatches || committingMatches || confirmingCompare} tip={isReadOnlyCompare ? "正在读取发布快照..." : "正在同步执行基线、匹配关系与确认状态..."}>
                <TimelineCompareView
                    ref={timelineRef}
                    viewportBinding={viewportBinding}
                    editingTrack={effectiveEditingTrack}
                    referenceTrack={activeReferenceTrack}
                    matches={matches}
                    readOnly={isReadOnlyCompare}
                    publicationReview={isReadOnlyCompare}
                    transactionSummaries={transactionSummaryQuery.data}
                    transactionSummaryLoading={transactionSummaryQuery.isFetching}
                    transactionSummaryError={transactionSummaryQuery.error}
                    airportConstraintsMap={airportConstraintsMap}
                    onMatchCreate={isReadOnlyCompare ? undefined : handleMatchCreate}
                    onMatchDelete={isReadOnlyCompare ? undefined : handleMatchDelete}
                    onMarkNew={isReadOnlyCompare ? undefined : handleMarkNew}
                    onMarkCancelled={isReadOnlyCompare ? undefined : handleMarkCancelled}
                    onMarkIgnore={isReadOnlyCompare ? undefined : handleMarkIgnore}
                    onHoverTargetChange={onHoverTargetChange}
                />
                <CompareMatchTable
                    editingTrack={effectiveEditingTrack}
                    referenceTrack={activeReferenceTrack}
                    matches={matches}
                    pairDirty={pairDirty}
                    readOnly={isReadOnlyCompare}
                    publicationReview={isReadOnlyCompare}
                    transactionSummaries={transactionSummaryQuery.data}
                    transactionSummaryLoading={transactionSummaryQuery.isFetching}
                    transactionSummaryError={transactionSummaryQuery.error}
                    onMatchDelete={isReadOnlyCompare ? undefined : handleMatchDelete}
                    onMarkNew={isReadOnlyCompare ? undefined : handleMarkNew}
                    onMarkCancelled={isReadOnlyCompare ? undefined : handleMarkCancelled}
                    onMarkIgnore={isReadOnlyCompare ? undefined : handleMarkIgnore}
                />
            </Spin>
        </Card>
    );
};

export default ScheduleDraftCompareSection;
